import { ShieldAlert } from "lucide-react";
import { EmptyState } from "@order-analyzer/ui";
import type { AppRole, Permission } from "@order-analyzer/shared";
import { useAuth } from "../auth.js";
import { useHasAnyPermission, useHasRole } from "../lib/permissions.js";

/**
 * Zentraler Zugriffsschutz für Routen, deren Seite ohne mindestens eine der übergebenen
 * Berechtigungen funktionslos wäre (z.B. Administration, Import und Export). Ergänzt das
 * bisherige, rein kosmetische Ausblenden des Navigationspunkts (siehe App.tsx navLinks) um eine
 * konsistente Rückmeldung bei Direktaufruf einer URL, statt einer leeren Seite oder rohen
 * 403-Fehlermeldungen aus einzelnen API-Aufrufen. Die serverseitige Durchsetzung (jede Route
 * einzeln über requirePermission/requireAnyPermission) bleibt davon unberührt die eigentliche
 * Sicherheitsgrenze - diese Komponente ist reine UX, kein Ersatz dafür.
 *
 * Mit `role` lässt sich eine Seite zusätzlich auf eine Rolle beschränken (z.B. Tätigkeiten,
 * Datenqualität, Import und Export nur für Administratoren, siehe adminOnly in App.tsx), auch
 * wenn andere Rollen die zugrunde liegende Berechtigung ebenfalls besitzen.
 */
export function RequirePermission({
  anyPermission,
  role,
  children,
}: {
  anyPermission?: Permission[];
  role?: AppRole;
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const hasPermission = useHasAnyPermission(anyPermission ?? []);
  const hasRole = useHasRole(role ?? "Administrator");
  const allowed = (!anyPermission || hasPermission) && (!role || hasRole);
  if (allowed) return <>{children}</>;
  return (
    <EmptyState
      icon={<ShieldAlert className="h-6 w-6" />}
      title="Kein Zugriff auf diesen Bereich"
      description={
        user
          ? `Ihre Rolle(n) (${user.roles.join(", ") || "keine"}) berechtigen nicht zum Aufruf dieser Seite. Wenden Sie sich an einen Administrator, falls Sie hier Zugriff benötigen.`
          : "Bitte melden Sie sich an, um diesen Bereich zu sehen."
      }
    />
  );
}
